import React, { useReducer } from 'react';
import UserContext from './userContext';
import UserReducer from './userReducer';
import api from '../../api/api';
import {
    GET_USERS,
    GET_USER,
    GET_AGENTS,
    UPDATE_USER,
    DELETE_USER,
    CREATE_USER,
    CLEAR_STATE,
    SET_LOADING,
    SET_AGENT,
    GET_AGENTS_FORM,
    CLEAR_OPERATORS,
    GET_OPERATORS,
    CLEAR_STATE_AGENTS_FORM,
    CLEAR_USER_SUCCESS,
} from '../types';
import { HEADERS } from '../../constants/headers';
import axios from 'axios';
import { USER_LEVEL_ACCESS } from 'src/components/access';
import { handleError } from '../utils/handleError';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';

const UserState = (props) => {
    const initialState = {
        users: [],
        user: {},
        loading: false,
        error: null,
        success: null,
        count: null,
        agents: [],
        operators: [],
        agentsForm: [],
    };

    const [state, dispatch] = useReducer(UserReducer, initialState);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();

    const messageAlertFunction = (message) => {
        enqueueSnackbar(t(message), { variant: 'error' });
    };

    const getUsers = async (values) => {
        setLoading();
        try {
            let res = await api.post(
                `/users/usersAr`,
                { ...values },
                HEADERS()
            );

            dispatch({
                type: GET_USERS,
                payload: res.data.results,
                count: res.data.total,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const setAgent = async (user) =>
        dispatch({ type: SET_AGENT, payload: user });

    const getAgents = async (query = {}) => {
        clearUserState();
        setLoading();
        try {
            const res = await api.post(
                `/users/searchDocuments`,
                { ...query, accessLevel: USER_LEVEL_ACCESS },
                HEADERS()
            );
            let { pagination = {}, results = [] } = res?.data;
            dispatch({ type: GET_AGENTS, payload: results, pagination });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getSupportTicketsUsers = async (query = {}) => {
        clearUserState();
        setLoading();
        try {
            const res = await api.post(
                `/users/searchDocuments`,
                { ...query },
                HEADERS()
            );
            let { pagination = {}, results = [] } = res?.data;
            dispatch({
                type: GET_AGENTS,
                payload: results,
                pagination,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getAgentsForm = async (query = {}) => {
        clearStateAgentsForm();
        setLoading();
        try {
            const res = await api.post(
                `/users/searchDocuments`,
                { ...query, accessLevel: USER_LEVEL_ACCESS },
                HEADERS()
            );
            let { pagination = {}, results = [] } = res?.data;
            dispatch({ type: GET_AGENTS_FORM, payload: results, pagination });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Get Single Item by ID
    const getUser = async (userId) => {
        setLoading();
        try {
            const res = await api.get(`/users/${userId}`, HEADERS());
            dispatch({
                type: GET_USER,
                payload: res.data.data,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Delete User
    const deleteUser = async (userId) => {
        setLoading();
        try {
            const res = await api.delete(`/users/${userId}`, HEADERS());
            dispatch({ type: DELETE_USER, payload: res.data.deletedId });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Create User
    const createUser = async (user) => {
        clearUserState();
        setLoading();
        try {
            const res = await api.post(`/users`, { ...user }, HEADERS());
            dispatch({ type: CREATE_USER, payload: res.data.data });
            enqueueSnackbar(t('SnackBar.UserCreated'), {
                variant: 'success',
            });
            history.push('/management/users');
        } catch (error) {
            dispatch(handleError(error, messageAlertFunction));
        }
    };

    //Update User
    const updateUser = async (user, userId, file) => {
        setLoading();
        try {
            let res = '';
            if (file) {
                const uploadConfig = await api.post(
                    '/uploads/image',
                    { type: file.type, fileName: file.name },
                    HEADERS()
                );

                await axios.put(uploadConfig.data.url, file, {
                    headers: { 'Content-Type': file ? file.type : null },
                });

                const dataKey = uploadConfig.data.key;

                res = await api.put(
                    `/users/${userId}`,
                    { ...user, image: dataKey },
                    HEADERS()
                );
            } else {
                res = await api.put(`/users/${userId}`, { ...user }, HEADERS());
            }
            dispatch({ type: UPDATE_USER, payload: res.data.data });
            return res.data.data;
        } catch (error) {
            dispatch(handleError(error));
            throw error;
        }
    };

    const regenerateUserPassword = async (userId) => {
        try {
            const res = await api.post(
                `/users/${userId}/regenerate-password`,
                {},
                HEADERS()
            );
            return res.data;
        } catch (error) {
            dispatch(handleError(error));
            throw error;
        }
    };

    //Clear State
    const clearUserState = () => dispatch({ type: CLEAR_STATE });
    const clearUserSuccess = () => dispatch({ type: CLEAR_USER_SUCCESS });
    const clearStateAgentsForm = () =>
        dispatch({ type: CLEAR_STATE_AGENTS_FORM });
    const clearOperators = () => dispatch({ type: CLEAR_OPERATORS });

    //Set Loading
    const setLoading = () => dispatch({ type: SET_LOADING });

    return (
        <UserContext.Provider
            value={{
                loading: state.loading,
                users: state.users,
                user: state.user,
                agents: state.agents,
                error: state.error,
                count: state.count,
                operators: state.operators,
                agentsForm: state.agentsForm,
                success: state.success,

                getUsers,
                getAgents,
                getUser,
                deleteUser,
                createUser,
                updateUser,
                regenerateUserPassword,
                clearUserState,
                clearUserSuccess,
                setLoading,
                getAgentsForm,
                setAgent,
                getSupportTicketsUsers,
            }}
        >
            {props.children}
        </UserContext.Provider>
    );
};

export default UserState;
